/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { DomPortalOutlet, PortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { ButtonModule } from 'primeng/button';
import { Store } from '@ngxs/store';
import { DeelnemersActions } from '@store/actions';
import { DeelnemersSelectors, OnderwijsLocatiesSelectors } from '@store/selectors';
import { Observable } from 'rxjs';
import { DeelnemerI } from '@models/deelnemer.model';
import { SettingsSelectors } from '@store/selectors/settings.selectors';
import { TableModule } from 'primeng/table';
import { TranslateModule } from '@ngx-translate/core';
import { OnderwijsLocatieI } from '@models/onderwijs-locatie.model';
import { PeriodsI } from '@models/periods';

@Component({
  selector: 'cito-doe-print-startcodes-layout',
  imports: [CommonModule, ButtonModule, TableModule, TranslateModule],
  templateUrl: './print-startcodes-layout.component.html',
  styleUrls: ['./print-startcodes-layout.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrintStartcodesLayoutComponent implements AfterViewInit {
  deelnemers$: Observable<DeelnemerI[]> = this.store.select(DeelnemersSelectors.getDeelnemersOnLocationId);
  // Create selector getDeelnemersOnLocationIdWithStartcodeAndPlanning
  deelnemersWithStartcodeAndPlanning$: Observable<DeelnemerI[]> = this.store.select(
    DeelnemersSelectors.getDeelnemersOnLocationIdWithStartcodeAndPlanning,
  );
  selectedOnderwijsLocatie$: Observable<OnderwijsLocatieI> = this.store.select(
    OnderwijsLocatiesSelectors.selectedOnderwijsLocatie,
  );
  settingsPeriods$: Observable<PeriodsI> = this.store.select(
    SettingsSelectors.settingsPeriods,
  ) as Observable<PeriodsI>;

  @ViewChild('printContentTemplate') printContentTemplateRef: any; // printable content.
  @ViewChild('printContentTable', { static: false }) printContentTableRef: any; // printable p-table.

  @ViewChild('iframe') iframe: any; // target host to render the printable content

  private portalHost!: PortalOutlet;
  private portal!: TemplatePortal;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private appRef: ApplicationRef,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private store: Store,
  ) {
  }

  ngAfterViewInit() {
    const iframe = this.iframe.nativeElement;

    this.portalHost = new DomPortalOutlet(
      iframe.contentDocument.body,
      this.appRef,
      this.injector,
    );

    this._attachStyles(iframe.contentWindow);
    this.portal = new TemplatePortal(this.printContentTemplateRef, this.viewContainerRef);

    // Attach portal to host
    this.portalHost.attach(this.portal);
    // Register after print for closing the component
    iframe.contentWindow.onafterprint = () => {
      this.store.dispatch(DeelnemersActions.PrintStartcodesDone);
    };
    // Allow some time for the component and table to be rendered
    setTimeout(() => {
      this.printMainContent();
    }, 1000);
  }

  printMainContent(): void {
    const iframe = this.iframe.nativeElement;
    iframe.contentWindow.print();
  }

  private _attachStyles(targetWindow: Window): void {
    // Copy styles from parent window
    document.querySelectorAll('style').forEach((htmlElement) => {
      targetWindow.document.head.appendChild(htmlElement.cloneNode(true));
    });
    // Copy stylesheet link from parent window
    const styleSheetElement = this._getStyleSheetElement();
    targetWindow.document.head.appendChild(styleSheetElement);
  }

  private _getStyleSheetElement() {
    const styleSheetElement = document.createElement('link');
    document.querySelectorAll('link').forEach((htmlElement) => {
      if (htmlElement.rel === 'stylesheet') {
        const absoluteUrl = new URL(htmlElement.href).href;
        styleSheetElement.rel = 'stylesheet';
        styleSheetElement.type = 'text/css';
        styleSheetElement.href = absoluteUrl;
      }
    });
    return styleSheetElement;
  }

  removeCampaignSuffixFromBusinessKey(businessKey: string): string {
    return businessKey.replace(/-\d{1,2}$/, '');
  }
}
